<template>
  <div class="lad-landing-itinerary__custom">
    <h2>{{ $t(`${base}.title`) }}</h2>

    <p class="description">{{ $t(`${base}.description`) }}</p>

    <el-button
      v-if="!isViatorPlatform"
      icon="el-icon-download"
      class="itinerary-download"
      @click="handleOpen"
      >{{ $t(`${base}.cta`) }}</el-button
    >

    <div class="itinerary-days">
      <el-collapse v-model="active" accordion>
        <div
          v-for="(day, i) in exItinerary"
          :key="`itinerary-day-${i}`"
          :class="[
            `timeline-content itinerary-day-${i}`,
            { 'hidden-day': limit < i + 1, 'is-last': limit === i + 1 },
          ]"
        >
          <div class="timeline-content__separator">
            <svg-icon
              :name="`landing/pointer`"
              :class="{ 'sprite-icons': true, active: active === i }"
            />

            <div class="separator"></div>
          </div>

          <el-collapse-item
            :title="
              $t(`${base}.day`, { day: i + 1, title: day.times[0].title })
            "
            :name="i"
          >
            <LazyHydrate never :trigger-hydration="i === active">
              <div>
                <el-carousel
                  v-if="day.images.length"
                  :height="isMobile ? '160px' : '320px'"
                  indicator-position="none"
                  arrow="never"
                >
                  <el-carousel-item
                    v-for="(image, k) in day.images"
                    :key="`itinerary-day-${i}-image-${k}`"
                  >
                    <LadImage
                      :src="image.url"
                      :srcset="image.srcset"
                      :include-all-sources="true"
                      :preloading="true"
                      :lazy-loading="false"
                    />
                  </el-carousel-item>
                </el-carousel>

                <div
                  v-for="(time, j) in day.times"
                  :key="`itinerary-day-${i}-time-${j}`"
                  :class="{ 'times-content': true, 'show-all': expanded === i }"
                  v-html="time.description"
                ></div>

                <el-button
                  type="text"
                  class="read-more"
                  @click="onSetExpanded(i)"
                  >{{
                    $t(`${base}.${expanded === i ? 'read_less' : 'read_more'}`)
                  }}</el-button
                >
              </div>
            </LazyHydrate>
          </el-collapse-item>
        </div>
      </el-collapse>
    </div>

    <div class="itinerary-show-more__wrapper">
      <el-button
        v-if="exItinerary.length > limit"
        class="itinerary-download"
        @click="limit = exItinerary.length"
        >{{ $t(`${base}.show_more_days`) }}</el-button
      >
    </div>

    <LadItineraryDownloadDialogZoho
      v-if="dialog.show"
      :open="dialog.show"
      @close="handleClose"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CacheComponentMixin from '@/utils/mixin/cache-components'

export default {
  name: 'LadLandingItineraryCustom',
  components: {
    LadItineraryDownloadDialogZoho: () =>
      import(
        /* webpackChunkName: "lad-itinerary-dialog-download-zoho" */ '@/components/itinerary/download/components/dialog-zoho'
      ),
  },
  mixins: [CacheComponentMixin],
  data() {
    return {
      base: 'landing.itinerary',
      dialog: {
        show: false,
      },
      active: 0,
      expanded: null,
      limit: 5,
    }
  },
  computed: {
    ...mapState('experience', {
      exItinerary: (state) => state.itinerary,
      isViatorPlatform: (state) => state.platform.name === 'Viator',
      exHighlights: 'highlights',
    }),
    ...mapGetters('device', ['isMobile']),
    getHighlights() {
      return this.exHighlights.join(' ')
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(newValue) {
        this.expanded = null
      },
    },
  },
  methods: {
    handleOpen() {
      this.dialog.show = true
    },
    handleClose() {
      this.dialog.show = false
    },
    onSetExpanded(index) {
      if (this.expanded === index) {
        this.expanded = null
        this.$scrollTo(`.itinerary-day-${index}`, 500)
      } else {
        this.expanded = index
      }
    },
  },
}
</script>

<style lang="scss">
.lad-landing-itinerary {
  &__custom {
    background-color: #fff;
    padding: 20px;

    h2 {
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;
      color: #221f20;
      margin: 0 0 10px;
    }

    .description {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      color: #221f20;
      margin: 0 0 20px;
    }

    .itinerary-download {
      width: 100%;
      background: unset;
      border: 1px solid #1d72af;
      color: #1d72af;
      font-size: 16px;
      font-weight: 500;
      line-height: 14px;
      padding: 16px;

      @media (min-width: 768px) {
        max-width: 253px;
      }
    }

    .itinerary-days {
      padding: 24px 0 40px;
    }

    .el-collapse {
      border: unset;
    }

    .timeline-content {
      gap: 16px;
      display: grid;
      grid-template-columns: 24px 1fr;

      &.hidden-day {
        display: none;
      }

      &.is-last {
        .separator {
          display: none;
        }
      }

      &__separator {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 24px 0 -24px;

        .sprite-icons {
          height: 24px;
          min-height: 24px;
          max-width: 20px;
          filter: brightness(0) saturate(100%) invert(82%) sepia(1%)
            saturate(1%) hue-rotate(180deg) brightness(97%) contrast(90%);

          &.active {
            filter: unset;
          }
        }

        .separator {
          height: 100%;
          width: 1px;
          border-left: 1px dashed #b2b1b1;
        }
      }

      .el-collapse {
        &-item {
          &__header {
            font-size: 20px;
            font-weight: 700;
            line-height: 24px;
            color: #221f20;
            height: auto;
            background: unset;
            padding: 24px 0;
            border-bottom: 1px solid #b2b1b1;

            &.is-active {
              border-bottom-color: transparent;
            }
          }

          &__content {
            padding: 0 0 32px;
          }

          &__wrap {
            background: unset;
            border-bottom: 1px solid #b2b1b1;

            .times-content {
              font-size: 16px;
              line-height: 24px;
              color: #221f20;
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;

              &.show-all {
                display: inline-block;
              }
            }

            .read-more {
              font-size: 14px;
              font-weight: 500;
              line-height: 21px;
              color: #221f20;
              text-decoration: underline;
            }

            .el-carousel {
              margin-bottom: 14px;

              &__item {
                img {
                  width: 100%;
                  max-height: 160px;
                  object-fit: cover;
                  border-radius: 10px;

                  @media (min-width: 768px) {
                    min-height: 320px;
                  }
                }
              }
            }
          }
        }
      }
    }

    .lad-itinerary-download--dialog {
      .el-dialog {
        margin-top: 1vh !important;

        &__body-wrapper {
          padding: 30px 25px;
          height: 100%;

          .lad-zoho--header {
            h2 {
              margin: 0.83em 0;
              color: #606266;
              line-height: 1.4;
            }

            span {
              color: #606266;
            }
          }
        }
      }
    }

    @media (min-width: 768px) {
      .itinerary-show-more__wrapper {
        display: flex;
        justify-content: center;
        margin-top: 15px;
      }
    }
  }
}
</style>
